import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Flame, Trophy, CheckCircle2, RotateCcw, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import QuizRunner from "@/components/QuizRunner";

export default function DailyChallenge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [streak, setStreak] = useState(0);
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    api.dailyChallenge()
      .then((d) => { setData(d); setStreak(d.streak || 0); })
      .catch(() => toast.error("Errore nel caricamento della sfida."))
      .finally(() => setLoading(false));
  }, []);

  const onComplete = async ({ score, total }) => {
    try {
      const res = await api.completeDaily({ score, total });
      setStreak(res.streak || 0);
      setData((d) => ({ ...d, completed: true }));
      toast.success(`Sfida completata! ${score}/${total}`);
    } catch { /* silent */ }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <Link to="/" data-testid="daily-back-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Torna ai materiali
      </Link>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-white">
              <Flame className="h-5 w-5" />
            </span>
            <div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight leading-none">Sfida Giornaliera</h1>
              <p className="text-sm text-muted-foreground mt-1">Mini-quiz sui tuoi argomenti più difficili</p>
            </div>
          </div>
          {streak > 0 && (
            <Badge data-testid="daily-streak" className="gap-1.5 bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900">
              <Flame className="h-3.5 w-3.5" /> {streak} {streak === 1 ? "giorno" : "giorni"} di fila
            </Badge>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
          {loading ? (
            <div className="text-center py-14">
              <Loader2 className="h-7 w-7 animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Preparo la sfida di oggi...</p>
            </div>
          ) : data?.empty || !data?.questions?.length ? (
            <div className="text-center py-12">
              <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground mb-4">Aggiungi materiali e completa qualche quiz: la sfida userà gli argomenti in cui sei più debole.</p>
              <Button asChild variant="secondary" className="rounded-xl"><Link to="/">Vai ai materiali</Link></Button>
            </div>
          ) : !started ? (
            <div className="text-center py-10">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-orange-100 dark:bg-orange-950/40 flex items-center justify-center mb-4">
                {data.completed ? <CheckCircle2 className="h-8 w-8 text-emerald-600" /> : <Trophy className="h-8 w-8 text-orange-500" />}
              </div>
              <h3 className="font-display text-lg font-bold mb-1">
                {data.completed ? "Hai già completato la sfida di oggi!" : "Pronto per la sfida di oggi?"}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">{data.questions.length} domande</p>
              {data.topics?.length > 0 && (
                <p className="text-xs text-muted-foreground mb-5">Argomenti: {data.topics.join(", ")}</p>
              )}
              <Button data-testid="start-daily-button" onClick={() => { setStarted(true); setRunKey((k) => k + 1); }} className="rounded-xl gap-2">
                {data.completed ? <RotateCcw className="h-4 w-4" /> : <Flame className="h-4 w-4" />}
                {data.completed ? "Rifai per esercizio" : "Inizia la sfida"}
              </Button>
            </div>
          ) : (
            <div>
              <QuizRunner key={runKey} questions={data.questions} timer onComplete={data.completed ? undefined : onComplete} />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
